import React, { useCallback, useMemo } from 'react';

import {
  Card,
  Image,
  ImageResizeOptions,
  PaddingModes,
  Text,
} from 'wix-ui-tpa';

import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import { useAppDispatch } from '@/store';
import { bookService } from '@/features';
import { EXPERIMENTS } from '@/constants';

import styleParams from 'AiAssistantWidget/stylesParams';

import {
  OtherPayload,
  PayloadAction,
  PayloadCmpProps,
} from '../ChatMessagePayload/types';
import { ChatCard } from '../ChatCard';
import { ImageBlock } from '../ImageBlock';
import {
  LinkButton,
  WidgetButton,
  WidgetButtonPriority,
  WidgetButtonSize,
} from '../WidgetButton';

import { classes, st } from './GenericPayload.st.css';

type GenericPayloadProps = PayloadCmpProps & {
  payload: OtherPayload;
};

export const GenericPayload: React.FC<
  GenericPayloadProps & React.HTMLAttributes<HTMLElement>
> = ({ isSmall, payload, actions, ...rootElementAttrs }) => {
  const dispatch = useAppDispatch();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const { title, subtitle, description, price, img, url, id } = payload;
  const styles = useStyles();

  const hasInfoBlock = !!(title || subtitle || description || price);

  const shouldRenderOptions = experiments.enabled(
    EXPERIMENTS.RENDER_AI_MESSAGE_OPTIONS,
  );

  const hasBookingAction = payload.actions?.includes(
    PayloadAction.BOOK_SERVICE,
  );

  const onClickBookService = useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(bookService({ id, name: title ?? id }));
  }, [dispatch, id, title]);

  const linkButtonTitle = useMemo(
    () =>
      ({
        product: t('app.widget.product.viewItem'),
        site_pages: t('app.widget.sitePages.viewItem'),
        blog_post: t('app.widget.blogPost.viewItem'),
        events: t('app.widget.events.viewItem'),
        online_program: t('app.widget.onlineProgram.viewItem'),
      }[payload.type] ?? t('app.widget.genericPayload.link')),
    [payload.type, t],
  );

  if (!img && !url && !hasInfoBlock) {
    return null;
  }

  // Button
  if (url && !img) {
    return (
      <div className={`${st(classes.root)} ${rootElementAttrs.className}`}>
        <LinkButton
          link={url}
          title={title ?? url}
          reportNavigationToAssistant={
            actions?.includes(PayloadAction.VIEW) ? { id } : undefined
          }
          priority={WidgetButtonPriority.secondary}
          size={WidgetButtonSize.tiny}
          // This prop is needed for ellipsis behavior
          paddingMode={PaddingModes.explicitPaddings}
        />
      </div>
    );
  }

  // Card
  return (
    <ChatCard
      {...rootElementAttrs}
      className={`${st(classes.root)} ${rootElementAttrs.className}`}
      isSmall={!!isSmall}
    >
      {img && (
        <Card.Container className={st(classes.section)}>
          <ImageBlock
            aspectRatio={styles.get(styleParams.productImageAspectRatio)}
            className={st(classes.imageBlock)}
          >
            <Image
              className={st(classes.image)}
              src={img}
              resize={ImageResizeOptions.cover}
              aspectRatio={styles.get(styleParams.productImageAspectRatio)}
            />
          </ImageBlock>
        </Card.Container>
      )}
      {hasInfoBlock && (
        <Card.Container className={st(classes.section)}>
          {title && <Text className={st(classes.title)}>{title}</Text>}
          {subtitle && (
            <Text className={st(classes.description)}>{subtitle}</Text>
          )}
          {description && (
            <Text className={st(classes.description)}>{description}</Text>
          )}
          {price && <Text className={st(classes.description)}>{price}</Text>}
        </Card.Container>
      )}
      {url && (
        <Card.Container className={st(classes.section, classes.sectionActions)}>
          <div className={st(classes.actionButtons)}>
            <LinkButton
              fullWidth
              link={url}
              reportNavigationToAssistant={
                actions?.includes(PayloadAction.VIEW) ? { id } : undefined
              }
              title={linkButtonTitle}
              size={WidgetButtonSize.tiny}
              // This prop is needed for ellipsis behavior
              paddingMode={PaddingModes.explicitPaddings}
              priority={WidgetButtonPriority.basic}
            />
            {shouldRenderOptions && hasBookingAction && id && (
              <WidgetButton
                text={t('app.widget.product.bookService')}
                onClick={onClickBookService}
                priority={WidgetButtonPriority.secondary}
                size={WidgetButtonSize.tiny}
                paddingMode={PaddingModes.explicitPaddings}
                fullWidth
              />
            )}
          </div>
        </Card.Container>
      )}
    </ChatCard>
  );
};
